// ─────────────────────────────────────────────────────────────────
// Glass-i — Customer Price List API client (Stage 6PL-1b).
//
// Thin axios wrappers around /api/erp/masters/price-lists/*.
//
// Every WRITE here is master-only server-side (decision D7). The page hides
// the write controls from a non-master as a courtesy, but the gate that
// matters is `requireMaster` on the route — the UI is never the guard.
// ─────────────────────────────────────────────────────────────────

import client from '../../../../api/client';

const BASE = '/api/erp/masters/price-lists';

function unwrap(resp) {
  return resp && resp.data && resp.data.data !== undefined
    ? resp.data.data
    : resp && resp.data;
}

export const priceListApi = {
  list: (params = {}) => client.get(BASE, { params }).then(unwrap),
  get: (id) => client.get(`${BASE}/${encodeURIComponent(id)}`).then(unwrap),
  // The five master catalogues the rate tabs pick from, in one shape.
  // `search` filters ITEMS only (a catalogue can hold thousands); the other
  // four come back whole.
  masterOptions: (search = '') => client.get(`${BASE}/master-options`, { params: { search } }).then(unwrap),
  // Customer Master mapping (Stage 6PL-1 — the customer picks a CARD).
  assignable: () => client.get(`${BASE}/assignable`).then(unwrap),
  customerAssignments: (customerId) =>
    client.get(`${BASE}/customer-assignments`, { params: { customerId } }).then(unwrap),
  mapCustomer: (customerId, priceListId) =>
    client.post(`${BASE}/map-customer`, { customerId, priceListId: priceListId || null }).then(unwrap),
  events: (id, params = {}) => client.get(`${BASE}/${encodeURIComponent(id)}/events`, { params }).then(unwrap),

  create: (payload) => client.post(BASE, payload).then(unwrap),
  update: (id, payload) => client.put(`${BASE}/${encodeURIComponent(id)}`, payload).then(unwrap),

  // Saves the WHOLE rate set for a list — a row absent from `rates` is removed.
  // The tab posts what it is showing, so a merge would make deletion impossible.
  saveRates: (id, rates) => client.put(`${BASE}/${encodeURIComponent(id)}/rates`, { rates }).then(unwrap),

  addAssignment: (id, payload) => client.post(`${BASE}/${encodeURIComponent(id)}/assignments`, payload).then(unwrap),
  removeAssignment: (id, assignmentId) =>
    client.delete(`${BASE}/${encodeURIComponent(id)}/assignments/${encodeURIComponent(assignmentId)}`).then(unwrap),

  activate: (id) => client.post(`${BASE}/${encodeURIComponent(id)}/activate`).then(unwrap),
  archive: (id) => client.delete(`${BASE}/${encodeURIComponent(id)}`).then(unwrap),

  // Read-only "which lists reach this customer, and what would these refs
  // cost?" — the document badge and the "Why this rate?" ladder. POST because
  // a large document's refs will not fit in a query string.
  resolve: (payload) => client.post(`${BASE}/resolve`, payload).then(unwrap),

  // Stage 6PL-1e — CSV. Fetched through axios rather than a plain <a href>
  // because the download has to carry the JWT the interceptor adds; a bare
  // link would hit the endpoint unauthenticated and save a 401 body as a file.
  exportRatesCsv: (id) => client.get(`${BASE}/${encodeURIComponent(id)}/rates/export`, {
    responseType: 'blob'
  }),
  // `dryRun` parses and counts WITHOUT writing, so the confirmation can say how
  // many rows the file actually holds before anything is replaced.
  importRatesCsv: (id, text, dryRun) =>
    client.post(`${BASE}/${encodeURIComponent(id)}/rates/import`, { csv: text, dryRun: dryRun === true })
      .then(unwrap)
};

/** Save a fetched blob under `filename`, then release the object URL. */
export function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoked on the next tick: revoking synchronously can cancel the download
  // in some browsers before it has started reading the blob.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * The key a resolved ITEM rate is filed under, matching the backend's
 * `refKey(kind, refId, thicknessMm)`. Defined ONCE here so a caller never
 * hand-builds it — a key that differs by a character reads as "no override",
 * which looks exactly like a customer simply not having a special rate.
 */
export function itemRefKey(itemId) {
  return `ITEM:${String(itemId)}:`;
}

/**
 * Soft-fail resolve for the proforma editor: a transient master outage must
 * never break a quotation in progress, so this degrades to "no price list"
 * rather than throwing. The caller then keeps master rates, which is exactly
 * the behaviour when no price list exists at all.
 */
export async function resolvePricesSafe(payload) {
  try {
    const out = await priceListApi.resolve(payload);
    return {
      lists: Array.isArray(out && out.lists) ? out.lists : [],
      rates: (out && out.rates) || {}
    };
  } catch (_e) {
    return { lists: [], rates: {} };
  }
}

export default priceListApi;
